/**
 * AdminUsers — manage HidroVerse admin sign-ins.
 *
 *   /portal/admins
 *
 * Signed-in admins can:
 *   · See the current list of admin users
 *   · Add a new admin (email + initial password)
 *   · Reset another admin's password
 *   · Remove an admin (with safety rails — can't delete yourself, can't
 *     delete the last admin)
 *
 * Authentication: reuses `hv_admin_jwt` / `hv_admin_token` from localStorage.
 * The backend accepts both the legacy ADMIN_TOKEN and per-admin JWTs.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, Trash2, KeyRound, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_LEGACY = "hv_admin_token";
const STORAGE_JWT = "hv_admin_jwt";

function getToken() {
  return localStorage.getItem(STORAGE_JWT)
      || localStorage.getItem(STORAGE_LEGACY)
      || "";
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const token = getToken();
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Create form
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [rUsers, rMe] = await Promise.all([
        fetch(`${API}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/me`,    { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (rUsers.status === 401 || rUsers.status === 403) {
        localStorage.removeItem(STORAGE_JWT);
        localStorage.removeItem(STORAGE_LEGACY);
        navigate("/admin/login", { replace: true });
        return;
      }
      const d1 = await rUsers.json();
      const d2 = await rMe.json();
      setUsers(d1.users || []);
      setMe(d2.user || null);
    } catch (e) {
      setError("Could not reach the API.");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const createAdmin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/users`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: newEmail.trim().toLowerCase(),
          password: newPassword,
          name: newName.trim() || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not create admin");
      setNewEmail(""); setNewPassword(""); setNewName("");
      await load();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (user) => {
    const next = window.prompt(
      `Set a new password for ${user.email} (min 8 chars).\n\nLeave blank to cancel.`,
    );
    if (!next || next.length < 8) {
      if (next !== null && next.length > 0 && next.length < 8) {
        setError("Password must be at least 8 characters.");
      }
      return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/users/${user.id}/password`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ new_password: next }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not change password");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeAdmin = async (user) => {
    if (!window.confirm(`Remove ${user.email} as an admin? This cannot be undone.`)) return;
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/users/${user.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Could not remove admin");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem(STORAGE_JWT);
    localStorage.removeItem(STORAGE_LEGACY);
    navigate("/admin/login");
  };

  const isMe = (u) => me && me.email && u.email === me.email;

  return (
    <main
      className="min-h-screen px-6 py-10"
      style={{ background: "#FAFAF7", color: "#0C2A40" }}
      data-testid="admin-users"
    >
      <Helmet>
        <title>{`Admins — HidroVerse`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="font-mono uppercase"
                 style={{ fontSize: "0.6rem", letterSpacing: "0.22em",
                          color: "rgba(12,42,64,0.6)" }}>
              HidroVerse · internal
            </div>
            <h1 style={{ fontSize: "1.6rem", fontWeight: 700, marginTop: 4 }}>
              Admin management
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {me && me.email && me.email !== "(legacy token)" && (
              <span className="font-mono uppercase"
                    style={{ fontSize: "0.6rem", letterSpacing: "0.18em",
                             color: "rgba(12,42,64,0.55)" }}>
                Signed in as {me.email}
              </span>
            )}
            <button
              type="button"
              onClick={signOut}
              data-testid="admin-users-signout"
              className="font-mono uppercase"
              style={{ fontSize: "0.62rem", letterSpacing: "0.18em",
                       color: "rgba(12,42,64,0.65)",
                       borderBottom: "1px dashed rgba(12,42,64,0.35)",
                       background: "transparent", border: "none", cursor: "pointer" }}
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Cross-nav */}
        <div className="flex border-b mb-6 flex-wrap items-center"
             style={{ borderColor: "rgba(12,42,64,0.12)" }}>
          <Link to="/portal/access" data-testid="admin-users-tab-access"
                className="px-4 py-2.5 font-mono uppercase"
                style={{ fontSize: "0.65rem", letterSpacing: "0.18em",
                         fontWeight: 700, color: "rgba(12,42,64,0.60)",
                         textDecoration: "none" }}>
            Tool access · downloads
          </Link>
          <span data-testid="admin-users-tab-current"
                className="px-4 py-2.5 font-mono uppercase"
                style={{ fontSize: "0.65rem", letterSpacing: "0.18em",
                         fontWeight: 700, color: "#0C2A40",
                         borderBottom: "2px solid #1FB6C1" }}>
            Admins
          </span>
          <Link to="/portal/content-planner" data-testid="admin-users-tab-content-planner"
                className="ml-auto inline-flex items-center gap-2 px-4 py-2 font-mono uppercase"
                style={{ fontSize: "0.62rem", letterSpacing: "0.18em",
                         fontWeight: 700, color: "#FFFFFF",
                         background: "#0C2A40", border: "1px solid #0C2A40",
                         textDecoration: "none", borderRadius: 999,
                         marginBottom: 6 }}>
            Open Content Planner →
          </Link>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3"
               style={{ background: "rgba(184,92,56,0.10)",
                        border: "1px solid rgba(184,92,56,0.35)",
                        color: "#B85C38", fontSize: "0.85rem", borderRadius: 10 }}
               data-testid="admin-users-error">
            {error}
          </div>
        )}

        {/* Add admin */}
        <section className="mb-6 p-5"
                 style={{ background: "#FFFFFF",
                          border: "1px solid rgba(12,42,64,0.10)", borderRadius: 12, boxShadow: "0 1px 2px rgba(12,42,64,0.04), 0 10px 28px -16px rgba(12,42,64,0.10)" }}>
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={14} style={{ color: "#0E7A82" }} />
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                           color: "#0E7A82", fontWeight: 700 }}>
              Add a new admin
            </span>
          </div>
          <form onSubmit={createAdmin} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <input type="email" placeholder="email@hidroverse.co.za"
                   value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                   required data-testid="admin-users-new-email"
                   className="px-3 py-2 outline-none"
                   style={fieldStyle} />
            <input type="text" placeholder="Name (optional)"
                   value={newName} onChange={(e) => setNewName(e.target.value)}
                   data-testid="admin-users-new-name"
                   className="px-3 py-2 outline-none"
                   style={fieldStyle} />
            <input type="password" placeholder="Initial password (min 8)"
                   value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                   required minLength={8} data-testid="admin-users-new-password"
                   className="px-3 py-2 outline-none"
                   style={fieldStyle} />
            <button type="submit" disabled={busy}
                    data-testid="admin-users-new-submit"
                    className="px-3 py-2 font-mono uppercase"
                    style={{ background: "#1FB6C1", color: "#0C2A40",
                             fontSize: "0.62rem", letterSpacing: "0.18em",
                             fontWeight: 700, border: "none",
                             cursor: busy ? "wait" : "pointer",
                             opacity: busy ? 0.5 : 1, borderRadius: 999 }}>
              {busy ? "Working…" : "Add admin"}
            </button>
          </form>
          <p className="mt-3 font-light"
             style={{ fontSize: "0.75rem", color: "rgba(12,42,64,0.6)",
                      lineHeight: 1.5 }}>
            The new admin receives no automatic email — share the initial
            password with them through a secure channel (WhatsApp, Signal),
            and ask them to sign in once and immediately reset their
            password via this page.
          </p>
        </section>

        {/* List admins */}
        <section data-testid="admin-users-list-section">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={14} style={{ color: "rgba(12,42,64,0.65)" }} />
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                           color: "rgba(12,42,64,0.65)", fontWeight: 700 }}>
              Current admins · {users.length}
            </span>
          </div>

          {loading && (
            <p className="font-mono uppercase"
               style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                        color: "rgba(12,42,64,0.55)" }}>
              Loading…
            </p>
          )}

          {!loading && users.length === 0 && (
            <p className="font-light"
               style={{ fontSize: "0.9rem", color: "rgba(12,42,64,0.65)" }}>
              No admins yet. Add the first one above (or use the master
              token at <Link to="/admin/login">/admin/login</Link>).
            </p>
          )}

          {!loading && users.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full"
                     style={{ borderCollapse: "collapse", background: "#FFFFFF",
                              border: "1px solid rgba(12,42,64,0.08)", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 2px rgba(12,42,64,0.04), 0 10px 28px -16px rgba(12,42,64,0.08)" }}
                     data-testid="admin-users-table">
                <thead>
                  <tr style={{ background: "#16181B", color: "#F5F7FA" }}>
                    <Th>Email</Th><Th>Name</Th><Th>Last sign-in</Th><Th>Created</Th><Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} style={{ borderTop: "1px solid rgba(12,42,64,0.06)" }}
                        data-testid={`admin-users-row-${u.email}`}>
                      <Td>
                        <span style={{ fontWeight: 500 }}>{u.email}</span>
                        {isMe(u) && (
                          <span className="ml-2 px-1.5 py-0.5 font-mono uppercase"
                                style={{ background: "rgba(31,182,193,0.15)",
                                         color: "#0E7A82", fontSize: "0.55rem",
                                         letterSpacing: "0.16em", fontWeight: 700 }}>
                            You
                          </span>
                        )}
                      </Td>
                      <Td>{u.name || "—"}</Td>
                      <Td>{u.last_login ? new Date(u.last_login).toLocaleString() : "—"}</Td>
                      <Td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</Td>
                      <Td>
                        <div className="flex gap-2 flex-wrap">
                          <button type="button"
                                  onClick={() => resetPassword(u)}
                                  disabled={busy}
                                  data-testid={`admin-users-reset-${u.email}`}
                                  className="px-2.5 py-1 font-mono uppercase inline-flex items-center gap-1"
                                  style={{ background: "transparent", color: "#0C2A40",
                                           border: "1px solid rgba(12,42,64,0.30)",
                                           fontSize: "0.6rem", letterSpacing: "0.15em",
                                           fontWeight: 700, cursor: busy ? "wait" : "pointer", borderRadius: 999 }}>
                            <KeyRound size={11} /> Reset password
                          </button>
                          {!isMe(u) && (
                            <button type="button"
                                    onClick={() => removeAdmin(u)}
                                    disabled={busy}
                                    data-testid={`admin-users-delete-${u.email}`}
                                    className="px-2.5 py-1 font-mono uppercase"
                                    style={{ background: "transparent", color: "#B85C38",
                                             border: "1px solid rgba(184,92,56,0.40)",
                                             fontSize: "0.6rem", letterSpacing: "0.15em",
                                             fontWeight: 700, cursor: busy ? "wait" : "pointer", borderRadius: 999 }}>
                              <Trash2 size={11} />
                            </button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="mt-6 font-light"
           style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.55)",
                    lineHeight: 1.55 }}>
          The legacy master <span className="font-mono">ADMIN_TOKEN</span> from
          the backend <span className="font-mono">.env</span> remains valid as
          a break-glass key on every admin endpoint, so you can never lock
          yourself out. Keep it secret — anyone who holds it can reset
          passwords via the first-time-setup flow.
        </p>
      </div>
    </main>
  );
}

const fieldStyle = {
  background: "#FAFAF7",
  border: "1px solid rgba(12,42,64,0.20)",
  color: "#0C2A40",
  fontSize: "0.85rem",
};

function Th({ children }) {
  return (
    <th className="px-3 py-2.5 text-left font-mono uppercase"
        style={{ fontSize: "0.6rem", letterSpacing: "0.18em", fontWeight: 700 }}>
      {children}
    </th>
  );
}
function Td({ children }) {
  return <td className="px-3 py-2.5"
             style={{ fontSize: "0.85rem", verticalAlign: "middle" }}>{children}</td>;
}
